import { useEffect, useRef } from 'react';
import i18n from '@/i18n/config';

/**
 * usePanelLocale - Forces English (LTR) locale for Admin & Seller panels
 * 
 * BUSINESS DECISION:
 * Admin & Seller panels must ALWAYS be English + LTR only.
 * This is enforced regardless of:
 * - User's browser language
 * - LocalStorage language preference
 * - URL parameters (?lang=he, etc.)
 * - User profile language setting
 * 
 * DO NOT modify this logic when adding new locales to the storefront.
 */
export function usePanelLocale() {
  const previousLanguageRef = useRef<string | null>(null);
  const previousDirRef = useRef<string | null>(null);

  useEffect(() => {
    // Store previous settings to restore when leaving panel
    previousLanguageRef.current = i18n.language;
    previousDirRef.current = document.documentElement.dir;

    // Force English language
    if (i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }

    // Force LTR direction
    document.documentElement.dir = 'ltr';
    document.documentElement.lang = 'en';

    // Add class to body for additional CSS control
    document.body.classList.add('force-ltr', 'panel-english-only');

    // Cleanup: restore previous settings when unmounting (leaving panel)
    return () => {
      document.body.classList.remove('force-ltr', 'panel-english-only');
      
      // Restore previous language/direction when leaving the panel
      if (previousLanguageRef.current && previousLanguageRef.current !== 'en') {
        i18n.changeLanguage(previousLanguageRef.current);
        document.documentElement.dir = previousDirRef.current || 'ltr';
        document.documentElement.lang = previousLanguageRef.current;
      }
    };
  }, []);

  // Return info for components that might need it
  return {
    language: 'en' as const,
    direction: 'ltr' as const,
    isEnglishOnly: true,
  };
}

/**
 * Helper to check if current path is an admin/seller panel
 * Used by components that need to conditionally render (e.g., language switcher)
 */
export function isPanelRoute(pathname: string): boolean {
  return (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/seller')
  );
}
